import PropTypes, { shape } from "prop-types";
import { useEffect, useState, useRef, useLayoutEffect } from "react";
import { Arrow, Circle, Group } from "react-konva";
import { Html } from "react-konva-utils";
import "../styles.css";
import { ELEMENT } from "../../../../util/ElementTypes";
import { isPrimaryMouseButton } from "../../utils/pointerButtonUtils";

// utils.js
export const convertAbsoluteToRelative = (points, x = 0, y = 0) =>
  points.map((coord, i) => (i % 2 === 0 ? coord - x : coord - y));

export const convertRelativeToAbsolute = (points, x = 0, y = 0) =>
  points.map((coord, i) => (i % 2 === 0 ? coord + x : coord + y));

// Add min/max constants for width/height
const MIN_WIDTH = 30;
const MAX_WIDTH = 570;
const MIN_HEIGHT = 0;
const MAX_HEIGHT = 600;

const DoubleSidedArrowElement = ({
  arrow,
  isDraggable,
  onClick,
  onDoubleClick,
  onUpdate,
  strokeColor,
  onDragEnd,
  saveToHistory,
  forwardRef,
  isSelected,
  onSelect,
  onDragMove,
  onTransform,
  onTransformEnd,
  hideBlueCircles = false,
  hitStrokeWidth = 20, // Add a prop for hit detection width, default to 20
  setArrowState,
  isOffCanvas = false,
  sourceStageRef,
  effectiveZoomLevel = 1, // Zoom level for scaling elements
  isPreview = false,
  onCopyToOppositeCanvas,
  canvasWidth,
  canvasHeight,
  isElementBeingEdited = false,
  setPendingChanges = () => {},
}) => {
  const shapeRef = useRef();
  const groupRef = useRef();
  const [points, setPoints] = useState(() => {
    // Always treat incoming points as RELATIVE to arrow.x, arrow.y
    return arrow.points || [0, 0, 0, 0];
  });

  // Only update Group position when arrow prop actually changes (not during drag)
  useEffect(() => {
    // Skip updates during drag operations to prevent conflicts
    if (isDraggingRef.current || isDraggingPointRef.current) {
      return;
    }

    const expectedX = arrow.x || 0;
    const expectedY = arrow.y || 0;

    if (
      groupRef.current &&
      (groupRef.current.x() !== expectedX || groupRef.current.y() !== expectedY)
    ) {
      groupRef.current.x(expectedX);
      groupRef.current.y(expectedY);
      groupRef.current.getLayer()?.batchDraw();
    }
  }, [arrow.x, arrow.y, arrow.id]); // Only depend on x and y changes, not the entire arrow object

  const getRelativePoints = (points, x, y) =>
    points.map((coord, i) => (i % 2 === 0 ? coord - x : coord - y));
  // Keep internal points in sync with prop updates when not dragging
  useEffect(() => {
    if (isDraggingRef.current || isDraggingPointRef.current) {
      return;
    }
    setPoints(arrow.points || [0, 0, 0, 0]);
  }, [arrow.points]);

  const [mousePos, setMousePos] = useState(null);
  const skipMouseMoveRef = useRef(false);
  const isDraggingPointRef = useRef(false);
  const isDraggingRef = useRef(false);
  const lastSelectUpdateRef = useRef(0); // Add throttling for onSelect calls

  // Utility to clamp and normalize arrow points to allowed min/max box (like LineElement)
  function clampAndNormalizeArrow(x0, y0, x1, y1) {
    let dx = x1 - x0;
    let dy = y1 - y0;
    let width = Math.abs(dx);
    let height = Math.abs(dy);
    let signX = dx >= 0 ? 1 : -1;
    let signY = dy >= 0 ? 1 : -1;
    let clampedWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, width));
    let clampedHeight = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, height));
    // Always enforce clamped width/height in the returned points
    let newX1 = x0 + signX * clampedWidth;
    let newY1 = y0 + signY * clampedHeight;
    // Normalize so group is at top-left
    let minX = Math.min(x0, newX1);
    let minY = Math.min(y0, newY1);
    let points = [x0 - minX, y0 - minY, newX1 - minX, newY1 - minY];
    return {
      points,
      groupX: minX,
      groupY: minY,
      absPoints: [x0, y0, newX1, newY1],
    };
  }

  const updatePoint = (index, newPointLocalX, newPointLocalY, e) => {
    // 1. Update the dragged point in relative coordinates
    const currentRelativePoints = [...points];
    currentRelativePoints[index] = newPointLocalX;
    currentRelativePoints[index + 1] = newPointLocalY;

    // 2. Get current group position (use ref during drag, fallback to prop)
    const groupX = groupRef.current?.x() ?? arrow.x ?? 0;
    const groupY = groupRef.current?.y() ?? arrow.y ?? 0;

    // 3. Apply boundary constraints for main canvas - prevent control points from going outside
    let constrainedPoints = [...currentRelativePoints];
    
    if (!isOffCanvas && canvasWidth && canvasHeight) {
      const canvasPadding = 20;
      
      // Constrain only the dragged point within canvas boundaries
      const absPointX = newPointLocalX + groupX;
      const absPointY = newPointLocalY + groupY;
      
      const constrainedAbsX = Math.max(
        canvasPadding,
        Math.min(canvasWidth - canvasPadding, absPointX)
      );
      const constrainedAbsY = Math.max(
        canvasPadding,
        Math.min(canvasHeight - canvasPadding, absPointY)
      );
      
      // Convert back to relative coordinates
      constrainedPoints[index] = constrainedAbsX - groupX;
      constrainedPoints[index + 1] = constrainedAbsY - groupY;
    }

    // 4. Calculate bounds and dimensions from constrained points
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    for (let i = 0; i < constrainedPoints.length; i += 2) {
      minX = Math.min(minX, constrainedPoints[i]);
      maxX = Math.max(maxX, constrainedPoints[i]);
      minY = Math.min(minY, constrainedPoints[i + 1]);
      maxY = Math.max(maxY, constrainedPoints[i + 1]);
    }

    let width = maxX - minX;
    let height = maxY - minY;

    // 5. Apply width constraint only if needed and not during end-point stretching
    // Use actual canvas width instead of hardcoded value
    const maxDisplayWidth = canvasWidth ? canvasWidth - 40 : 570; // Account for padding
    const isStretchingFromEnd = index >= constrainedPoints.length - 2;
    const isStretchingFromStart = index <= 1;

    // Only apply width clamping if arrow exceeds max width AND we're not stretching from ends
    // AND we're not off-canvas
    if (width > maxDisplayWidth && !isStretchingFromEnd && !isStretchingFromStart && !isOffCanvas) {
      const centerX = (minX + maxX) / 2;
      const scale = maxDisplayWidth / (width || 1);

      // Scale all points relative to the center
      for (let i = 0; i < constrainedPoints.length; i += 2) {
        const relativeToCenter = constrainedPoints[i] - centerX;
        constrainedPoints[i] = centerX + relativeToCenter * scale;
      }

      width = maxDisplayWidth;

      // Recalculate height after width scaling
      minY = Infinity;
      maxY = -Infinity;
      for (let i = 1; i < constrainedPoints.length; i += 2) {
        minY = Math.min(minY, constrainedPoints[i]);
        maxY = Math.max(maxY, constrainedPoints[i]);
      }
      height = maxY - minY;
    }

    // 6. Only adjust arrow position when dragging start or end points
    // For middle waypoints, keep the group position fixed to prevent visual shifting
    const isDraggingStartOrEnd = isStretchingFromStart || isStretchingFromEnd;
    
    let newX = groupX;
    let newY = groupY;
    
    if (isDraggingStartOrEnd) {
      if (isStretchingFromStart) {
        // When dragging START point, keep the END point fixed in absolute position
        // So we need to adjust the group position to keep the end point in place
        const endPointIndex = points.length - 2;
        const oldEndX = points[endPointIndex];
        const oldEndY = points[endPointIndex + 1];
        const absoluteEndX = groupX + oldEndX;
        const absoluteEndY = groupY + oldEndY;
        
        // The new end point position in the constrained points
        const newEndX = constrainedPoints[endPointIndex];
        const newEndY = constrainedPoints[endPointIndex + 1];
        
        // Adjust group position to keep end point in the same absolute position
        newX = absoluteEndX - newEndX;
        newY = absoluteEndY - newEndY;
      } else if (isStretchingFromEnd) {
        // When dragging END point, keep the START point fixed in absolute position
        // So we keep the group position fixed (or adjust minimally to keep start point in place)
        const oldStartX = points[0];
        const oldStartY = points[1];
        const absoluteStartX = groupX + oldStartX;
        const absoluteStartY = groupY + oldStartY;
        
        // The new start point position in the constrained points
        const newStartX = constrainedPoints[0];
        const newStartY = constrainedPoints[1];
        
        // Adjust group position to keep start point in the same absolute position
        newX = absoluteStartX - newStartX;
        newY = absoluteStartY - newStartY;
      }
    }


    // 7. Update the points state with constrained relative points
    setPoints(constrainedPoints);

    // 8. Convert to absolute points for onUpdate (but adjust group position to preserve absolute top-left)
    const absPoints = constrainedPoints.map((coord, i) =>
      i % 2 === 0 ? coord + newX : coord + newY
    );

    // 9. Call onUpdate with new absolute points but keep original group position
    onUpdate(
      arrow.id,
      absPoints, // Pass the calculated absolute points
      (constrainedPoints.length - 4) / 2,
      arrow.angle,
      newX,
      newY,
      width,
      e
    );

    // 10. Update the selected object with new dimensions if arrow is selected
    if (isSelected && onSelect) {
      // Throttle onSelect calls to prevent too frequent updates during drag
      const now = Date.now();
      const throttleDelay = 16; // Reduced to ~60fps for smoother updates

      if (now - lastSelectUpdateRef.current > throttleDelay) {
        // Create updated arrow object with new dimensions
        const updatedArrow = {
          id: arrow.id,
          type: "DoubleSidedArrow",
          strokeWidth: arrow.strokeWidth || 2,
          isDashed: arrow.isDashed || false,
          bend: arrow.bend || 0,
          angle: arrow.angle || 90,
          points: absPoints, // Use absolute points for selection
          fillColor: arrow.fillColor || "#000000",
          strokeColor:
            arrow.stroke || arrow.strokeColor || strokeColor || "#000000",
          stroke: arrow.stroke || arrow.strokeColor || strokeColor || "#000000",
          x: newX,
          y: newY,
          width,
          height,
          rotation: arrow.rotation || 0,
          name: arrow.name,
        };

        // Create a synthetic event to prevent deselection during drag
        const syntheticEvent = {
          target: { attrs: { type: "DoubleSidedArrow", id: arrow.id } },
          evt: { shiftKey: false },
        };

        // Call onSelect to update the selected object with synthetic event
        onSelect(syntheticEvent, updatedArrow);
        lastSelectUpdateRef.current = now;
      }
    }
  };

  const recalculateWaypoints = (existingPoints, angle) => {
    if (existingPoints.length < 4) return existingPoints;

    const newPoints = [existingPoints[0], existingPoints[1]]; // Start point
    for (let i = 2; i < existingPoints.length - 2; i += 2) {
      const prevX = newPoints[newPoints.length - 2];
      const prevY = newPoints[newPoints.length - 1];

      const deltaX = existingPoints[i] - prevX;
      const deltaY = existingPoints[i + 1] - prevY;

      let midX, midY;
      if (angle === 90) {
        midX = prevX;
        midY = prevY + deltaY;
      } else {
        const radians = (angle * Math.PI) / 180;
        midX = prevX + deltaX * Math.cos(radians) - deltaY * Math.sin(radians);
        midY = prevY + deltaX * Math.sin(radians) + deltaY * Math.cos(radians);
      }

      newPoints.push(midX, midY);
    }
    newPoints.push(
      existingPoints[existingPoints.length - 2],
      existingPoints[existingPoints.length - 1]
    ); // End point
    return newPoints;
  };

  // Sync points with arrow.points when they change (from transformer)
  // Only sync when the arrow prop actually changes, not during drag operations
  useEffect(() => {
    // Skip if currently dragging to prevent interference
    if (isDraggingRef.current || isDraggingPointRef.current) {
      return;
    }

    if (arrow.points && arrow.points.length >= 4) {
      // Check if points are absolute or relative
      // For arrows with bends, we need a more sophisticated detection
      const firstPointX = arrow.points[0];
      const firstPointY = arrow.points[1];
      const arrowX = arrow.x || 0;
      const arrowY = arrow.y || 0;

      // Improved logic to detect relative vs absolute points
      // Relative points typically have a mix of positive/negative values and smaller magnitudes
      // Absolute points typically have larger positive values and are more spread out
      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;
      for (let i = 0; i < arrow.points.length; i += 2) {
        minX = Math.min(minX, arrow.points[i]);
        maxX = Math.max(maxX, arrow.points[i]);
        minY = Math.min(minY, arrow.points[i + 1]);
        maxY = Math.max(maxY, arrow.points[i + 1]);
      }

      const rangeX = maxX - minX;
      const rangeY = maxY - minY;
      const avgMagnitude =
        (Math.abs(minX) + Math.abs(maxX) + Math.abs(minY) + Math.abs(maxY)) / 4;

      // Consider points relative if:
      // 1. The range is reasonable (not too large)
      // 2. Average magnitude is not too large
      // 3. OR if we have both positive and negative values (typical of relative coordinates)
      const hasMixedSigns = (minX < 0 && maxX > 0) || (minY < 0 && maxY > 0);
      const isRelative =
        (rangeX < 1000 && rangeY < 1000 && avgMagnitude < 500) || hasMixedSigns;

      if (isRelative) {
        // Points are already relative (starting with 0,0 or close to it)

        setPoints(arrow.points);
      } else {
        // Convert absolute points to relative
        const relativePoints = arrow.points.map((coord, i) =>
          i % 2 === 0 ? coord - arrowX : coord - arrowY
        );

        setPoints(relativePoints);
      }
    } else {
      setPoints(arrow.points || [0, 0, 0, 0]);
    }
  }, [arrow.points]); // Only depend on arrow.points, not x and y

  // If x or y changes from props, update the Group position
  useLayoutEffect(() => {
    // Skip updates during drag operations to prevent conflicts
    if (isDraggingRef.current || isDraggingPointRef.current) {
      return;
    }

    const newX = arrow.x || 0;
    const newY = arrow.y || 0;

    // Also manually update the Group's position using ref
    if (groupRef.current) {
      groupRef.current.x(newX);
      groupRef.current.y(newY);
      groupRef.current.getLayer()?.batchDraw();
    }
  }, [arrow.x, arrow.y]);

  // Handle arrow selection similar to the onClick function in MyCanvas
  const handleArrowSelect = (e) => {
    if (!onSelect) return;

    // Ensure we have a valid event object
    if (!e) {
      return;
    }

    const target = e.target;
    const id = arrow.id;
    const existingPoints = points; // Use internal relative points

    // Calculate bounds of the arrow from points
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    for (let i = 0; i < existingPoints.length; i += 2) {
      minX = Math.min(minX, existingPoints[i]);
      maxX = Math.max(maxX, existingPoints[i]);
      minY = Math.min(minY, existingPoints[i + 1]);
      maxY = Math.max(maxY, existingPoints[i + 1]);
    }

    const width = maxX - minX;
    const height = maxY - minY;

    // Get arrow's current position (from state)
    const x = arrow.x || 0;
    const y = arrow.y || 0;
    const rotation = arrow.rotation || 0;

    // Create the selected arrow object with all needed properties
    const selectedArrow = {
      id,
      type: "DoubleSidedArrow",
      strokeWidth: arrow.strokeWidth || 2,
      isDashed: arrow.isDashed || false,
      bend: arrow.bend || 0,
      angle: arrow.angle || 90,
      points: points.map((coord, i) => (i % 2 === 0 ? coord + x : coord + y)), // Convert internal relative points to absolute for selection
      fillColor: arrow.fillColor || "#000000",
      strokeColor:
        arrow.stroke || arrow.strokeColor || strokeColor || "#000000",
      stroke: arrow.stroke || arrow.strokeColor || strokeColor || "#000000",
      x,
      y,
      width,
      height,
      rotation,
      name: arrow.name,
    };

    // Call the onSelect callback with the arrow data
    onSelect(e, selectedArrow);
  };

  // Create the anchor points (circles) at the start and end of the arrow
  const renderAnchors = () => {
    if (points.length < 4) return null;

    // Create anchors only for the start and end points
    const anchors = [];
    const anchorRadius = 5;

    // Start point anchor
    anchors.push(
      <Circle
        key="start-anchor"
        x={points[0]}
        y={points[1]}
        radius={anchorRadius}
        fill="white"
        stroke="black"
        strokeWidth={Math.max(1, 1 / Math.max(0.3, effectiveZoomLevel || 1))}
        draggable={false}
      />
    );

    // End point anchor
    anchors.push(
      <Circle
        key="end-anchor"
        x={points[points.length - 2]}
        y={points[points.length - 1]}
        radius={anchorRadius}
        fill="white"
        stroke="black"
        strokeWidth={Math.max(1, 1 / Math.max(0.3, effectiveZoomLevel || 1))}
        draggable={false}
      />
    );

    return anchors;
  };

  const handleMouseMove = (e) => {
    // Skip during control point dragging
    if (isDraggingPointRef.current) return;

    // Skip onClick during mouse move to prevent unwanted selection
    // and don't trigger updates that could cause resizing
    skipMouseMoveRef.current = true;

    const stage = e.target.getStage();
    const pos = stage.getPointerPosition();

    let minDist = Infinity;
    let closestPoint = null;

    // Important: Use local position in the group's coordinate system
    // This fixes the misalignment when arrow is resized
    const groupNode = e.target.getParent() || e.target;
    const transform = groupNode.getAbsoluteTransform().copy().invert();
    const localPos = transform.point(pos);

    for (let i = 0; i < points.length - 2; i += 2) {
      const x1 = points[i];
      const y1 = points[i + 1];
      const x2 = points[i + 2];
      const y2 = points[i + 3];

      const dx = x2 - x1;
      const dy = y2 - y1;
      const len2 = dx * dx + dy * dy;

      if (len2 === 0) continue;

      const t = Math.max(
        0,
        Math.min(1, ((localPos.x - x1) * dx + (localPos.y - y1) * dy) / len2)
      );
      const projX = x1 + t * dx;
      const projY = y1 + t * dy;

      const dist = Math.sqrt(
        (localPos.x - projX) * (localPos.x - projX) +
          (localPos.y - projY) * (localPos.y - projY)
      );

      if (dist < minDist && dist < 10) {
        minDist = dist;
        closestPoint = { x: projX, y: projY };
      }
    }

    setMousePos(closestPoint);

    // Reset skipMouseMoveRef after processing to allow red circle to appear
    skipMouseMoveRef.current = false;
  };

  const handleMouseEnter = () => {
    skipMouseMoveRef.current = true;
  };

  const handleMouseLeave = () => {
    skipMouseMoveRef.current = false;
    setMousePos(null);
  };

  return (
    <Group
      ref={groupRef}
      id={arrow.id}
      name={arrow.name}
      height={arrow.height}
      x={arrow.x || 0}
      y={arrow.y || 0}
      scaleX={1}
      scaleY={1}
      rotation={arrow.rotation || 0}
      draggable={isDraggable}
      perfectDrawEnabled={false}
      shadowForStrokeEnabled={false}
      onDragStart={(e) => {
        isDraggingRef.current = true;
        e.target.setAttr("dragDistance", 0);
      }}
      onDragMove={(e) => {
        const node = e.target;
        if (!node) return;
        const newX = node.x();
        const newY = node.y();

        // Update the group position during drag
        onDragMove?.(
          {
            ...arrow,
            id: arrow.id,
            x: newX,
            y: newY,
            points: points, // Keep current relative points
            width: arrow.width,
            height: arrow.height,
            rotation: arrow.rotation || 0,
            text: arrow.text,
          },
          e
        );
      }}
      // Transformer functionality removed - users will use control points to resize
      // Transformer functionality removed - users will use control points to resize
      onDragEnd={(e) => {
        const node = e.target;
        const newX = node.x();
        const newY = node.y();
        
        // CRITICAL: Get current rotation from the Konva node, not from stale state
        // The node always has the current visual rotation
        let nodeRotation = node.rotation();
        nodeRotation = nodeRotation % 360;
        if (nodeRotation < 0) nodeRotation += 360;

        // Create currentArrow with properties from node (rotation, scaleX, scaleY, position)
        // CRITICAL: Set rotation AFTER spreading arrow to ensure it overrides any stale values
        const currentArrow = {
          ...arrow,
          x: newX,
          y: newY,
          // CRITICAL: These must come AFTER the spread to override any stale values from arrow
          rotation: nodeRotation, // Use the rotation from node
          scaleX: node.scaleX(),
          scaleY: node.scaleY(),
        };

        // Debug logging to verify rotation is preserved

        isDraggingRef.current = false;
        onDragEnd(arrow.id, newX, newY, sourceStageRef, currentArrow);
      }}
    >
      {/* Double-sided Arrow with hit detection */}
      <Arrow
        transformsEnabled={false}
        id={arrow.id}
        ref={shapeRef}
        name={arrow.name}
        points={points}
        length={arrow.length}
        stroke={arrow.stroke || arrow.strokeColor || strokeColor}
        strokeWidth={arrow.strokeWidth || 2}
        hitStrokeWidth={Math.max(10, (arrow.strokeWidth || 2) + 8)}
        fill={arrow.stroke || arrow.strokeColor || strokeColor}
        pointerAtBeginning={true}
        pointerAtEnding={true}
        rotation={arrow.rotation || 0}
        draggable={false}
        perfectDrawEnabled={true}
        listening={true}
        shadowForStrokeEnabled={false}
        lineCap="round"
        lineJoin="round"
        className="konva-arrow"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
        onClick={(e) => {
          if (!isPrimaryMouseButton(e.evt)) {
            return;
          }
          e.cancelBubble = true;
          e.target.attrs.type = "DoubleSidedArrow";
          handleArrowSelect?.(e);
        }}
        // Transformer functionality removed - users will use control points to resize
      />
      {/* Blue anchor circles, etc. */}
      {/* Red circle bend creation removed - now handled by ArrowEditor */}

      {/* Only show blue circles when arrow is selected */}
      {isSelected &&
        !hideBlueCircles &&
        points.map((_, index) =>
          index % 2 === 0 ? (
            <Circle
              key={index}
              x={points[index]}
              y={points[index + 1]}
              radius={6} // ← increased from 6
              fill="blue"
              name={arrow.name}
              stroke="white"
              strokeWidth={Math.max(
                1.5,
                1.5 / Math.max(0.3, effectiveZoomLevel || 1)
              )}
              perfectDrawEnabled={false}
              shadowForStrokeEnabled={false}
              draggable
              onDragStart={(e) => {
                isDraggingPointRef.current = true;
                e.cancelBubble = true;
                // Don't update point on drag start, only on move and end
              }}
              onDragMove={(e) => {
                e.cancelBubble = true;
                isDraggingPointRef.current = true;

                // Get the local position of the dragged circle
                let localX = e.target.x();
                let localY = e.target.y();

                // Apply boundary constraints for main canvas before updating
                // Get current group position (use ref during drag, fallback to prop)
                const groupX = groupRef.current?.x() ?? arrow.x ?? 0;
                const groupY = groupRef.current?.y() ?? arrow.y ?? 0;
                
                if (!isOffCanvas && canvasWidth && canvasHeight) {
                  const canvasPadding = 20;
                  
                  // Calculate absolute position of the dragged point
                  const absPointX = localX + groupX;
                  const absPointY = localY + groupY;
                  
                  // Constrain the point within canvas boundaries
                  const constrainedAbsX = Math.max(
                    canvasPadding,
                    Math.min(canvasWidth - canvasPadding, absPointX)
                  );
                  const constrainedAbsY = Math.max(
                    canvasPadding,
                    Math.min(canvasHeight - canvasPadding, absPointY)
                  );
                  
                  // Convert back to relative coordinates
                  localX = constrainedAbsX - groupX;
                  localY = constrainedAbsY - groupY;
                  
                  // Update the circle's visual position to match the constrained position
                  e.target.x(localX);
                  e.target.y(localY);
                }

                // Use the existing updatePoint function for consistency
                updatePoint(index, localX, localY, e);
              }}
              onDragEnd={(e) => {
                e.cancelBubble = true;

                // Get the local position of the dragged circle
                let localX = e.target.x();
                let localY = e.target.y();

                // Apply boundary constraints for main canvas before updating
                // Get current group position (use ref during drag, fallback to prop)
                const groupX = groupRef.current?.x() ?? arrow.x ?? 0;
                const groupY = groupRef.current?.y() ?? arrow.y ?? 0;
                
                if (!isOffCanvas && canvasWidth && canvasHeight) {
                  const canvasPadding = 20;
                  
                  // Calculate absolute position of the dragged point
                  const absPointX = localX + groupX;
                  const absPointY = localY + groupY;
                  
                  // Constrain the point within canvas boundaries
                  const constrainedAbsX = Math.max(
                    canvasPadding,
                    Math.min(canvasWidth - canvasPadding, absPointX)
                  );
                  const constrainedAbsY = Math.max(
                    canvasPadding,
                    Math.min(canvasHeight - canvasPadding, absPointY)
                  );
                  
                  // Convert back to relative coordinates
                  localX = constrainedAbsX - groupX;
                  localY = constrainedAbsY - groupY;
                  
                  // Update the circle's visual position to match the constrained position
                  e.target.x(localX);
                  e.target.y(localY);
                }

                // Use the existing updatePoint function for consistency
                updatePoint(index, localX, localY, e);

                // Force a final onSelect call to ensure the selected object is updated
                if (isSelected && onSelect) {
                  // Get updated points from state (they were updated by updatePoint)
                  const updatedPoints = points; // This will be the latest after updatePoint
                  
                  // Calculate final dimensions and bounding box
                  let minX = Infinity,
                    minY = Infinity,
                    maxX = -Infinity,
                    maxY = -Infinity;
                  for (let i = 0; i < updatedPoints.length; i += 2) {
                    minX = Math.min(minX, updatedPoints[i]);
                    maxX = Math.max(maxX, updatedPoints[i]);
                    minY = Math.min(minY, updatedPoints[i + 1]);
                    maxY = Math.max(maxY, updatedPoints[i + 1]);
                  }
                  const finalWidth = maxX - minX;
                  const finalHeight = maxY - minY;

                  // Calculate current bounding box to get the adjusted position
                  let currentMinX = Infinity,
                    currentMinY = Infinity;
                  const currentGroupX = groupRef.current?.x() ?? arrow.x ?? 0;
                  const currentGroupY = groupRef.current?.y() ?? arrow.y ?? 0;
                  
                  // Get the original bounding box from arrow props (before drag)
                  const originalPoints = arrow.points || [0, 0, 0, 0];
                  for (let i = 0; i < originalPoints.length; i += 2) {
                    currentMinX = Math.min(currentMinX, originalPoints[i]);
                    currentMinY = Math.min(currentMinY, originalPoints[i + 1]);
                  }
                  
                  // Calculate adjusted position to preserve absolute top-left
                  const absoluteTopLeftX = (arrow.x || 0) + currentMinX;
                  const absoluteTopLeftY = (arrow.y || 0) + currentMinY;
                  const finalX = absoluteTopLeftX - minX;
                  const finalY = absoluteTopLeftY - minY;

                  const finalArrow = {
                    id: arrow.id,
                    type: "DoubleSidedArrow",
                    strokeWidth: arrow.strokeWidth || 2,
                    isDashed: arrow.isDashed || false,
                    bend: arrow.bend || 0,
                    angle: arrow.angle || 90,
                    points: updatedPoints.map((coord, i) =>
                      i % 2 === 0 ? coord + finalX : coord + finalY
                    ),
                    fillColor: arrow.fillColor || "#000000",
                    strokeColor:
                      arrow.stroke ||
                      arrow.strokeColor ||
                      strokeColor ||
                      "#000000",
                    stroke:
                      arrow.stroke ||
                      arrow.strokeColor ||
                      strokeColor ||
                      "#000000",
                    x: finalX,
                    y: finalY,
                    width: finalWidth,
                    height: finalHeight,
                    rotation: arrow.rotation || 0,
                    name: arrow.name,
                  };

                  // Create a synthetic event to prevent deselection during drag end
                  const syntheticEvent = {
                    target: {
                      attrs: { type: "DoubleSidedArrow", id: arrow.id },
                    },
                    evt: { shiftKey: false },
                  };

                  onSelect(syntheticEvent, finalArrow);
                }

                // CRITICAL: Don't save to history here - history will be saved when element is unselected
                // This groups all drag and transform changes into a single history entry
                // Reset dragging state after a short delay to prevent flicker
                setTimeout(() => {
                  isDraggingPointRef.current = false;
                }, 100);
              }}
            />
          ) : null
        )}

      {/* {isSelected && renderAnchors()} */}

      {/* Copy button for off-canvas double-sided arrows */}
      {(() => {
        // Copy button commented out
        const shouldShowCopyButton = false &&
          isSelected &&
          !isPreview &&
          isOffCanvas &&
          typeof onCopyToOppositeCanvas === "function";

        if (!shouldShowCopyButton) return null;

        // Calculate bounding box for arrow (points are relative to Group)
        if (!points || points.length < 4) return null;
        const xCoords = points.filter((_, i) => i % 2 === 0);
        const yCoords = points.filter((_, i) => i % 2 === 1);
        const minX = Math.min(...xCoords);
        const maxX = Math.max(...xCoords);
        const minY = Math.min(...yCoords);
        const maxY = Math.max(...yCoords);
        
        // Position relative to the Group (arrow.x and arrow.y are the Group position)
        // Button should be at top-right of the arrow's bounding box
        const copyButtonX = maxX - 14; // Right edge of arrow minus button width
        const copyButtonY = minY - 36; // Above the arrow

        return (
          <Html
            x={copyButtonX}
            y={copyButtonY}
            divProps={{
              style: {
                pointerEvents: "none",
                zIndex: 30,
              },
            }}
          >
            <div
              style={{
                pointerEvents: "none",
              }}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onCopyToOppositeCanvas?.();
                }}
                title="Copy to Main Canvas"
                style={{
                  width: "26px",
                  height: "26px",
                  borderRadius: "4px",
                  border: "1px solid #CBD5E1",
                  backgroundColor: "#FFFFFF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 10px rgba(15, 23, 42, 0.18)",
                  cursor: "pointer",
                  pointerEvents: "auto",
                  padding: 0,
                  transition:
                    "background-color 0.15s ease, border-color 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#F1F5F9";
                  e.currentTarget.style.borderColor = "#94A3B8";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#FFFFFF";
                  e.currentTarget.style.borderColor = "#CBD5E1";
                }}
              >
                <svg
                  width="16"
                  height="19"
                  viewBox="0 0 17 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.97572 15.75C5.47055 15.75 5.04297 15.575 4.69297 15.225C4.34297 14.875 4.16797 14.4474 4.16797 13.9423V2.55775C4.16797 2.05258 4.34297 1.625 4.69297 1.275C5.04297 0.925 5.47055 0.75 5.97572 0.75H14.3602C14.8654 0.75 15.293 0.925 15.643 1.275C15.993 1.625 16.168 2.05258 16.168 2.55775V13.9423C16.168 14.4474 15.993 14.875 15.643 15.225C15.293 15.575 14.8654 15.75 14.3602 15.75H5.97572ZM5.97572 14.25H14.3602C14.4372 14.25 14.5077 14.2179 14.5717 14.1538C14.6359 14.0898 14.668 14.0192 14.668 13.9423V2.55775C14.668 2.48075 14.6359 2.41025 14.5717 2.34625C14.5077 2.28208 14.4372 2.25 14.3602 2.25H5.97572C5.89872 2.25 5.82822 2.28208 5.76422 2.34625C5.70005 2.41025 5.66797 2.48075 5.66797 2.55775V13.9423C5.66797 14.0192 5.70005 14.0898 5.76422 14.1538C5.82822 14.2179 5.89872 14.25 5.97572 14.25ZM2.47572 19.25C1.97055 19.25 1.54297 19.075 1.19297 18.725C0.842969 18.375 0.667969 17.9474 0.667969 17.4423V4.55775H2.16797V17.4423C2.16797 17.5193 2.20005 17.5898 2.26422 17.6538C2.32822 17.7179 2.39872 17.75 2.47572 17.75H12.3602V19.25H2.47572Z"
                    fill="#1E293B"
                  />
                </svg>
              </button>
            </div>
          </Html>
        );
      })()}
    </Group>
  );
};

export default DoubleSidedArrowElement;
